/**
 * IndexedDB caching utility for shop-the-look product data
 * Caches both product JSON and rendered HTML partials
 */

class IndexedDBCache {
  constructor(dbName = 'shopify-stl-cache', version = 1) {
    this.dbName = dbName;
    this.version = version;
    this.db = null;
    this.stores = {
      products: 'products',
      htmlPartials: 'html-partials',
    };

    // Cache expiry times (in milliseconds)
    this.expiry = {
      products: 30 * 60 * 1000, // 30 minutes
      htmlPartials: 15 * 60 * 1000, // 15 minutes
    };
  }

  /**
   * Initialize the database
   */
  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create product store
        if (!db.objectStoreNames.contains(this.stores.products)) {
          const productStore = db.createObjectStore(this.stores.products, { keyPath: 'handle' });
          productStore.createIndex('id', 'data.id', { unique: false });
          productStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Create HTML partials store
        if (!db.objectStoreNames.contains(this.stores.htmlPartials)) {
          const htmlStore = db.createObjectStore(this.stores.htmlPartials, { keyPath: 'handle' });
          htmlStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Check if cached data is still valid
   */
  isValid(timestamp, type) {
    const now = Date.now();
    const expiry = this.expiry[type] || this.expiry.products;
    return (now - timestamp) < expiry;
  }

  /**
   * Get product data from cache
   */
  async getProduct(handle) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.stores.products], 'readonly');
      const store = transaction.objectStore(this.stores.products);
      const request = store.get(handle);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const { result } = request;

        if (!result || !this.isValid(result.timestamp, 'products')) {
          resolve(null);
          return;
        }

        resolve(result.data);
      };
    });
  }

  /**
   * Store product data in cache
   */
  async setProduct(handle, productData) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.stores.products], 'readwrite');
      const store = transaction.objectStore(this.stores.products);

      const cacheEntry = {
        handle,
        data: productData,
        timestamp: Date.now(),
      };

      const request = store.put(cacheEntry);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(true);
    });
  }

  /**
   * Get HTML partial from cache
   */
  async getHtmlPartial(handle) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.stores.htmlPartials], 'readonly');
      const store = transaction.objectStore(this.stores.htmlPartials);
      const request = store.get(handle);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const { result } = request;

        if (!result || !this.isValid(result.timestamp, 'htmlPartials')) {
          resolve(null);
          return;
        }

        resolve(result.html);
      };
    });
  }

  /**
   * Store HTML partial in cache
   */
  async setHtmlPartial(handle, html) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.stores.htmlPartials], 'readwrite');
      const store = transaction.objectStore(this.stores.htmlPartials);

      const cacheEntry = {
        handle,
        html,
        timestamp: Date.now(),
      };

      const request = store.put(cacheEntry);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(true);
    });
  }

  /**
   * Clear expired entries from cache
   */
  async clearExpired() {
    await this.init();

    const stores = Object.values(this.stores);

    // eslint-disable-next-line no-restricted-syntax
    for (const storeName of stores) {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const index = store.index('timestamp');

      const expiry = this.expiry[storeName === this.stores.products ? 'products' : 'htmlPartials'];
      const cutoff = Date.now() - expiry;

      const request = index.openCursor(IDBKeyRange.upperBound(cutoff));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
    }
  }

  /**
   * Clear all cache data
   */
  async clearAll() {
    await this.init();

    const stores = Object.values(this.stores);

    // eslint-disable-next-line no-restricted-syntax
    for (const storeName of stores) {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      store.clear();
    }
  }
}

// Initialize cache cleanup on page load
const cacheInstance = new IndexedDBCache();

// Clean expired entries every 30 minutes
setInterval(() => {
  cacheInstance.clearExpired().catch((error) => {
    console.warn('Cache cleanup failed:', error);
  });
}, 30 * 60 * 1000);

// Clean expired entries on page load (but wait a bit to not block initial load)
setTimeout(() => {
  cacheInstance.clearExpired().catch((error) => {
    console.warn('Initial cache cleanup failed:', error);
  });
}, 5000);

// Export singleton instance
export default cacheInstance;
